import { AlertTriangle, CheckCircle2, Clock, Info, XCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { SCORE_STYLES, scoreBand } from "@/lib/format";

export const Panel = ({ title, subtitle, actions, children, className = "", testid }) => (
  <section className={`panel ${className}`} data-testid={testid}>
    {(title || actions) && (
      <div className="panel-header">
        <div className="min-w-0">
          {title && (
            <h2 className="font-display text-[13px] font-semibold tracking-tight text-slate-800">
              {title}
            </h2>
          )}
          {subtitle && <p className="mt-0.5 text-[11px] text-slate-500">{subtitle}</p>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
    )}
    {children}
  </section>
);

export const KpiCard = ({ label, value, sub, tone = "default", icon: Icon, testid }) => {
  const tones = {
    default: "text-slate-900",
    positive: "text-emerald-700",
    warning: "text-amber-700",
    danger: "text-red-700",
    muted: "text-slate-400",
  };
  return (
    <div
      className="panel px-3 py-2.5 transition-shadow duration-150 hover:shadow-sm"
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="label-xs">{label}</span>
        {Icon && <Icon className="h-3.5 w-3.5 text-slate-400" />}
      </div>
      <div className={`mono mt-1.5 text-xl font-semibold leading-none ${tones[tone]}`}>{value}</div>
      {sub && <div className="mt-1.5 text-[11px] leading-tight text-slate-500">{sub}</div>}
    </div>
  );
};

const SCORE_ADVICE_TONE = {
  GREEN: "text-emerald-700",
  ORANGE: "text-amber-700",
  RED: "text-red-700",
  UNKNOWN: "text-slate-500",
};

export const ScoreBadge = ({ score, advice, size = "md", testid }) => {
  const band = scoreBand(score);
  const cls = SCORE_STYLES[band];
  const isLg = size === "lg";
  return (
    <div className="inline-flex flex-col items-start gap-0.5" data-testid={testid}>
      <span
        className={`chip ${cls} ${isLg ? "px-2.5 py-1 text-[15px]" : "text-[12px]"} mono font-bold`}
      >
        {score === null || score === undefined ? "—" : `${score} / 100`}
      </span>
      <span
        className={`text-[9px] font-bold uppercase tracking-[0.06em] ${
          SCORE_ADVICE_TONE[band] || SCORE_ADVICE_TONE.UNKNOWN
        }`}
      >
        {advice || "ONVOLDOENDE DATA"}
      </span>
    </div>
  );
};

const STATUS_STYLE = {
  CONNECTED: { cls: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: CheckCircle2 },
  NOT_CONFIGURED: { cls: "bg-red-100 text-red-800 border-red-300", icon: XCircle },
  ERROR: { cls: "bg-red-100 text-red-800 border-red-300", icon: XCircle },
  RATE_LIMITED: { cls: "bg-amber-100 text-amber-800 border-amber-300", icon: Clock },
  LATER_BESCHIKBAAR: { cls: "bg-slate-100 text-slate-600 border-slate-300", icon: Clock },
};

export const StatusBadge = ({ status, label, testid }) => {
  const style = STATUS_STYLE[status] || STATUS_STYLE.LATER_BESCHIKBAAR;
  const Icon = style.icon;
  return (
    <span className={`chip ${style.cls}`} data-testid={testid}>
      <Icon className="h-3 w-3" />
      {label || status}
    </span>
  );
};

const confidenceTone = (value) => {
  if (value >= 75) return "bg-emerald-600";
  if (value >= 60) return "bg-blue-600";
  if (value >= 40) return "bg-amber-500";
  return "bg-red-500";
};

export const ConfidenceBar = ({ value, label }) => {
  const v = value ?? 0;
  const tone = confidenceTone(v);
  return (
    <div className="min-w-[92px]">
      <div className="mono text-[12px] font-semibold text-slate-800">
        {value === null || value === undefined ? "—" : `${v}%`}
      </div>
      <div className="mt-0.5 h-1 w-full overflow-hidden rounded-sm bg-slate-200">
        <div className={`h-full ${tone} transition-[width] duration-300`} style={{ width: `${v}%` }} />
      </div>
      {label && <div className="mt-0.5 text-[10px] leading-tight text-slate-500">{label}</div>}
    </div>
  );
};

export const NullValue = ({ children = "Niet beschikbaar" }) => (
  <span className="text-[12px] italic text-slate-400">{children}</span>
);

export const InlineWarning = ({ children, tone = "warning", testid }) => {
  const tones = {
    warning: "border-amber-300 bg-amber-50 text-amber-900",
    danger: "border-red-300 bg-red-50 text-red-900",
    info: "border-blue-300 bg-blue-50 text-blue-900",
  };
  const Icon = tone === "info" ? Info : AlertTriangle;
  return (
    <div
      className={`flex items-start gap-2 rounded-sm border px-2.5 py-2 text-[12px] ${tones[tone]}`}
      data-testid={testid}
    >
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <div className="min-w-0">{children}</div>
    </div>
  );
};

export const AssumptionTag = () => (
  <span className="chip border-slate-300 bg-slate-100 text-slate-600">Standaard aanname</span>
);

const ORIGIN_TAG = {
  MANUAL_ENTRY: { cls: "border-blue-300 bg-blue-50 text-blue-800", label: "Handmatig ingevoerd" },
  LIVE_API: { cls: "border-emerald-300 bg-emerald-50 text-emerald-800", label: "Live data" },
};

export const OriginTag = ({ origin }) => {
  const tag = ORIGIN_TAG[origin];
  if (!tag) return null;
  return <span className={`chip ${tag.cls}`}>{tag.label}</span>;
};

export const NotConfiguredState = ({
  providers = [],
  title = "Live AutoScout24-koppeling nog niet geconfigureerd",
  description = "Er worden geen voertuigen opgehaald en er wordt géén demo- of fictieve data gegenereerd. De reken-engines (BPM, waardering, kosten, Deal Score) zijn volledig operationeel zodra echte advertentiedata beschikbaar is.",
  testid = "not-configured-state",
}) => (
  <div className="panel grid-lines p-6" data-testid={testid}>
    <div className="mx-auto max-w-2xl">
      <div className="mb-3 flex items-center gap-2">
        <XCircle className="h-4 w-4 text-red-600" />
        <span className="label-xs text-red-700">Databron niet actief</span>
      </div>
      <h3 className="font-display text-lg font-semibold tracking-tight text-slate-900">{title}</h3>
      <p className="mt-2 max-w-xl text-[13px] leading-relaxed text-slate-600">{description}</p>

      {providers.length > 0 && (
        <div className="mt-4 divide-y divide-slate-200 border border-slate-200 bg-white">
          {providers.map((p) => (
            <div
              key={p.key || p.provider}
              className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
            >
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-slate-800">
                  {p.label || p.provider}
                </div>
                {p.message && <div className="text-[11px] text-slate-500">{p.message}</div>}
              </div>
              <StatusBadge status={p.status} label={p.status_label} />
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Link
          to="/integraties"
          data-testid="goto-integrations"
          className="inline-flex items-center gap-2 rounded-sm bg-blue-600 px-3 py-1.5 text-[12px] font-semibold text-white transition-colors duration-150 hover:bg-blue-700"
        >
          Naar Integraties
        </Link>
        <Link
          to="/integraties?tab=ingest"
          data-testid="goto-manual-ingest"
          className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-50"
        >
          Echte advertentie handmatig invoeren
        </Link>
      </div>
    </div>
  </div>
);

export const EmptyState = ({ title, description, action, testid }) => (
  <div className="panel px-4 py-8 text-center" data-testid={testid}>
    <h3 className="font-display text-[14px] font-semibold text-slate-800">{title}</h3>
    {description && (
      <p className="mx-auto mt-1.5 max-w-md text-[12px] text-slate-500">{description}</p>
    )}
    {action && <div className="mt-3 flex justify-center">{action}</div>}
  </div>
);

export const ListingLinkButton = ({ url, size = "sm", testid }) =>
  url ? (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center gap-1 rounded-sm bg-slate-900 font-semibold text-white transition-colors duration-150 hover:bg-slate-700 ${
        size === "lg" ? "px-3 py-2 text-[13px]" : "px-2 py-1 text-[11px]"
      }`}
    >
      Bekijk advertentie
    </a>
  ) : (
    <span className="text-[11px] italic text-slate-400">Geen directe URL</span>
  );
